import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Layout } from './components/Layout';
import { LanguageSelector } from './components/LanguageSelector';
import { Landing } from './pages/Landing';
import { Auth } from './pages/Auth';
import { PatientDashboard } from './pages/PatientDashboard';
import { SymptomSelect } from './pages/SymptomSelect';
import { SwipeDiagnosis } from './pages/SwipeDiagnosis';
import { DiagnosisResults } from './pages/DiagnosisResults';
import { PatientHistory } from './pages/PatientHistory';
import { MedicalDashboard } from './pages/MedicalDashboard';
import { QuestionManagement } from './pages/QuestionManagement';
import { SymptomManagement } from './pages/SymptomManagement';
import { ProductManagement } from './pages/ProductManagement';
import { Settings } from './pages/Settings';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to={user.role === 'medical_professional' ? '/medical' : '/dashboard'} replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to={user.role === 'medical_professional' ? '/medical' : '/dashboard'} replace />;
  }

  return children;
};

// Guest route - accessible without login, shows layout if logged in
const GuestRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If user is logged in, wrap with Layout
  if (user) {
    return <Layout>{children}</Layout>;
  }

  // Guest mode - no layout wrapper
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />

      {/* Guest accessible routes (no login required) */}
      <Route path="/symptoms" element={<GuestRoute><SymptomSelect /></GuestRoute>} />
      <Route path="/diagnose/:symptomId" element={<GuestRoute><SwipeDiagnosis /></GuestRoute>} />
      <Route path="/results" element={<GuestRoute><DiagnosisResults /></GuestRoute>} />

      {/* Patient routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <PatientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/results/:diagnosisId" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <DiagnosisResults />
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute allowedRoles={['patient']}>
          <PatientHistory />
        </ProtectedRoute>
      } />

      {/* Medical professional routes */}
      <Route path="/medical" element={
        <ProtectedRoute allowedRoles={['medical_professional']}>
          <MedicalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/medical/questions" element={
        <ProtectedRoute allowedRoles={['medical_professional']}>
          <QuestionManagement />
        </ProtectedRoute>
      } />
      <Route path="/medical/symptoms" element={
        <ProtectedRoute allowedRoles={['medical_professional']}>
          <SymptomManagement />
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <MedicalDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/products" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ProductManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/questions" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <QuestionManagement />
        </ProtectedRoute>
      } />
      <Route path="/admin/symptoms" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <SymptomManagement />
        </ProtectedRoute>
      } />

      {/* Shared routes */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <Settings />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <LanguageSelector />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
